import { useT } from "../../../shared/i18n/i18n";
import { Loader2 } from "lucide-react";
import { usePublicView } from "../../../features/view-shares/model/use-public-view";
import { cn } from "../../../shared/lib/cn";

// Anonymous read-only board view behind a capability URL: no session, the
// token is the whole authorization. Renders list and kanban layouts; every
// other view type falls back to the list.

type PublicField = {
  config: { progress?: { max: number }; ratingScale?: number };
  id: string;
  name: string;
  options: Array<{ color: string; id: string; value: string }>;
  type: string;
};

type PublicItem = {
  fieldValues: Array<{ fieldDefinitionId: string; value: string | string[] }>;
  statusId: string;
  title: string;
  workObjectId: string;
};

export function PublicViewPage({ token }: { token: string }) {
  const t = useT();
  const view = usePublicView(token);

  return (
    <main className="min-h-screen bg-background px-4 py-8">
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-4">
        {view.isPending ? (
          <div className="flex justify-center py-20">
            <Loader2 className="size-5 animate-spin text-muted-foreground" />
          </div>
        ) : view.isError || !view.data ? (
          <div className="rounded-lg border border-border/70 bg-card p-8 text-center">
            <h1 className="text-base font-semibold">{t("publicPage.viewUnavailable")}</h1>
            <p className="mt-1.5 text-sm text-muted-foreground">{t("publicPage.linkDisabled")}</p>
          </div>
        ) : (
          <>
            <div className="flex items-baseline justify-between gap-3">
              <div>
                <h1 className="text-lg font-semibold">{view.data.boardName}</h1>
                <p className="mt-0.5 text-sm text-muted-foreground">{view.data.viewName} · read-only</p>
              </div>
              <span className="text-xs text-muted-foreground/70">
                {view.data.items.length} item{view.data.items.length === 1 ? "" : "s"}
              </span>
            </div>
            {view.data.viewType === "kanban" ? (
              <PublicKanban fields={view.data.fields} items={view.data.items} statuses={view.data.statuses} />
            ) : (
              <PublicList fields={view.data.fields} items={view.data.items} statuses={view.data.statuses} />
            )}
          </>
        )}
      </div>
    </main>
  );
}

function PublicList({
  fields,
  items,
  statuses,
}: {
  fields: PublicField[];
  items: PublicItem[];
  statuses: Array<{ color: string; id: string; name: string }>;
}) {
  const t = useT();
  const statusById = new Map(statuses.map((status) => [status.id, status]));
  return (
    <div className="overflow-x-auto rounded-lg border border-border/70">
      <table className="w-full min-w-[40rem] border-collapse text-sm">
        <thead>
          <tr className="bg-muted/40 text-left text-xs text-muted-foreground">
            <th className="px-3 py-2 font-medium">{t("board.name")}</th>
            <th className="px-3 py-2 font-medium">{t("statuses.status")}</th>
            {fields.map((field) => (
              <th className="px-3 py-2 font-medium" key={field.id}>
                {field.name}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((item) => {
            const status = statusById.get(item.statusId);
            return (
              <tr className="border-t border-border/50" key={item.workObjectId}>
                <td className="px-3 py-2">{item.title}</td>
                <td className="px-3 py-2">
                  {status ? <StatusChip color={status.color} name={status.name} /> : "—"}
                </td>
                {fields.map((field) => (
                  <td className="px-3 py-2 text-muted-foreground" key={field.id}>
                    <PublicFieldValue field={field} item={item} />
                  </td>
                ))}
              </tr>
            );
          })}
          {items.length === 0 ? (
            <tr>
              <td className="px-3 py-6 text-center text-muted-foreground" colSpan={fields.length + 2}>
                {t("publicPage.noItems")}
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );
}

function PublicKanban({
  fields,
  items,
  statuses,
}: {
  fields: PublicField[];
  items: PublicItem[];
  statuses: Array<{ color: string; id: string; name: string }>;
}) {
  return (
    <div className="flex gap-3 overflow-x-auto pb-2">
      {statuses.map((status) => {
        const columnItems = items.filter((item) => item.statusId === status.id);
        return (
          <div className="w-64 shrink-0 rounded-lg bg-muted/30 p-2" key={status.id}>
            <div className="mb-2 flex items-center gap-2 px-1">
              <span className="size-2 rounded-full" style={{ backgroundColor: status.color }} />
              <span className="text-xs font-medium">{status.name}</span>
              <span className="text-xs text-muted-foreground/70">{columnItems.length}</span>
            </div>
            <div className="flex flex-col gap-1.5">
              {columnItems.map((item) => (
                <div className="rounded-md border border-border/60 bg-card px-2.5 py-2 text-sm" key={item.workObjectId}>
                  <div>{item.title}</div>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {fields.slice(0, 3).map((field) => {
                      const label = fieldLabel(field, item);
                      if (!label) return null;
                      return (
                        <span className="rounded bg-muted/50 px-1.5 py-0.5 text-[11px] text-muted-foreground" key={field.id}>
                          {label}
                        </span>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function StatusChip({ color, name }: { color: string; name: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-muted/50 px-2 py-0.5 text-xs">
      <span className="size-1.5 rounded-full" style={{ backgroundColor: color }} />
      {name}
    </span>
  );
}

function PublicFieldValue({ field, item }: { field: PublicField; item: PublicItem }) {
  const progress = field.config.progress;
  const rawValue = item.fieldValues.find((value) => value.fieldDefinitionId === field.id)?.value;
  const values = Array.isArray(rawValue) ? rawValue : rawValue ? [rawValue] : [];
  if (progress) {
    const numeric = Number(values[0]);
    const ratio = Number.isFinite(numeric) ? Math.min(Math.max(numeric / progress.max, 0), 1) : 0;
    return (
      <span className="flex min-w-24 items-center gap-2">
        <span className="h-1 flex-1 overflow-hidden rounded-full bg-muted">
          <span className={cn("block h-full rounded-full bg-primary")} style={{ width: `${ratio * 100}%` }} />
        </span>
        <span className="shrink-0 text-xs tabular-nums">{Math.round(ratio * 100)}%</span>
      </span>
    );
  }
  return <>{fieldLabel(field, item) || "—"}</>;
}

function fieldLabel(field: PublicField, item: PublicItem): string {
  const rawValue = item.fieldValues.find((value) => value.fieldDefinitionId === field.id)?.value;
  const values = Array.isArray(rawValue) ? rawValue : rawValue ? [rawValue] : [];
  if (values.length === 0) return "";
  if (field.type === "select" || field.type === "multi_select") {
    return values.map((value) => field.options.find((option) => option.id === value)?.value ?? "").filter(Boolean).join(", ");
  }
  if (field.type === "rating") {
    return `${values[0]}/${field.config.ratingScale ?? 5}`;
  }
  if (field.type === "percent") {
    return `${values[0]}%`;
  }
  if (field.type === "duration") {
    const seconds = Number(values[0]);
    if (!Number.isFinite(seconds)) return String(values[0]);
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.round((seconds % 3600) / 60);
    return hours > 0 ? (minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`) : `${minutes}m`;
  }
  if (field.type === "date_range") {
    return values.join(" — ");
  }
  if (field.type === "checkbox") {
    return values[0] === "true" ? "✓" : "";
  }
  if (field.type === "work_object_relation" || field.type === "work_object_multi_relation" || field.type === "user_relation" || field.type === "user_multi_relation") {
    // Ids would be meaningless to an anonymous reader.
    return values.length > 0 ? `${values.length} linked` : "";
  }
  return values.join(", ");
}
